import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { ScreenReaderOnly } from '../ScreenReaderOnly/ScreenReaderOnly';
import clsx from 'clsx';
import { debounce } from 'lodash';
import { useTranslation } from 'react-i18next';
import { useUniqueWcagValues } from 'common-react';
import { ReactComponent as WarningIcon } from 'common-assets/assets/icons/warningIconFilledOrange.svg';
import { ReactComponent as CheckmarkIcon } from 'common-assets/assets/icons/success-icon.svg';
import { ReactComponent as ErrorIcon } from 'common-assets/assets/icons/error-icon.svg';
import { ReactComponent as CloseIcon } from 'common-assets/assets/icons/close.svg';
import { Dialog } from '../Dialog/Dialog';
import { Icon } from '../Icon/Icon';
import { CircularSpinner } from '../CircularSpinner/CircularSpinner';
import { Typography } from '../Typography/Typography';
import { useIsMobile } from '../utils/useIsMobile';
import { useBodyScrollLock } from '../utils/useBodyScrollLock';
import { useFocusTrap } from '../utils/useFocusTrap';
import { ModalDialogIcon, MODAL_SIZE_WIDTH } from './tools/ModalDialog.const';
import { ModalDialogProps, MobileModalProps } from './tools/ModalDialog.model';
import { useTheme } from '../styles/styles';
import { MobileAppTopBar } from '../MobileAppTopBar/MobileAppTopBar';

import useStyles from './ModalDialog.style';

const MobileModal: React.FC<MobileModalProps> = ({
  onClose, footer, children, scrollRef, isScrollable, showTopBorder, showBottomBorder, showCloseButton = true,
}) => {
  const c = useStyles({});
  const [tt] = useTranslation();
  const focusTrapRef = useFocusTrap(onClose);
  const isMobile = useIsMobile();
  const theme = useTheme();

  useBodyScrollLock(isMobile);

  return (
    <>
      <MobileAppTopBar backgroundColor={theme.color.greys[0]} />
      <div ref={focusTrapRef} className={c.mobileContainer}>
        <div className={clsx(c.mobileTopBar, showTopBorder && c.mobileTopBarWithBorder)}>
          {showCloseButton && (
            <button className={c.mobileCloseButton} onClick={onClose} aria-label={tt('close-dialog')}>
              <Icon>
                <CloseIcon />
              </Icon>
            </button>
          )}
        </div>
        <div className={clsx(c.mobileScrollable, isScrollable && c.mobileScrollableExpanded)} ref={scrollRef}>
          {children}
        </div>
        <div className={clsx(c.mobileFooter, showBottomBorder && c.mobileFooterWithBorder)}>
          {footer}
        </div>
      </div>
    </>
  );
};

const ICON_MAP: Record<Exclude<ModalDialogIcon, 'loading' | null>, React.ReactNode> = {
  warning: <WarningIcon />,
  checkmark: <CheckmarkIcon />,
  error: <ErrorIcon />,
};

export const ModalDialog: React.FC<ModalDialogProps> = ({
  open,
  onClose,
  size = 'md',
  icon = 'warning',
  title,
  content,
  primaryAction,
  secondaryAction,
  customFooter,
  mobileVariant = 'modal',
  className,
  ariaDescribedBy,
  titleVisuallyHidden = false,
  disableBackdropClick = false,
}) => {
  const c = useStyles({});
  const isMobile = useIsMobile();
  const { getUniqueValue } = useUniqueWcagValues();
  const internalTitleId = getUniqueValue('modal-title');
  const [contentEl, setContentEl] = useState<HTMLElement | null>(null);
  const [showBottomDivider, setShowBottomDivider] = useState(false);
  const [showTopDivider, setShowTopDivider] = useState(false);
  const [isScrollable, setIsScrollable] = useState(false);

  useEffect(() => {
    if (!contentEl) return;

    const scrollable = contentEl.scrollHeight - contentEl.clientHeight > 1;
    setIsScrollable(scrollable);

    const checkDividers = () => {
      setShowBottomDivider(contentEl.scrollHeight - contentEl.scrollTop - contentEl.clientHeight > 1);
      setShowTopDivider(contentEl.scrollTop > 1);
    };

    checkDividers();

    const debouncedCheckDividers = debounce(() => checkDividers());
    const observer = new ResizeObserver(debouncedCheckDividers);
    observer.observe(contentEl);
    contentEl.addEventListener('scroll', checkDividers);

    return () => {
      debouncedCheckDividers.cancel();
      observer.disconnect();
      contentEl.removeEventListener('scroll', checkDividers);
    };
  }, [contentEl]);

  const hasActions = primaryAction || secondaryAction;
  const isLoading = icon === 'loading';

  const getHeaderIconEl = (): React.ReactNode => {
    if (isLoading) return <CircularSpinner size="small" className={c.spinnerIcon} />;
    if (icon !== null) return <Icon className={c.icon}>{ICON_MAP[icon]}</Icon>;
    return null;
  };

  const headerIconEl = getHeaderIconEl();

  const scrollableContent = (
    <>
      <div className={clsx(c.headerContainer, isLoading && c.headerContainerSpinner)}>
        {headerIconEl}
        {titleVisuallyHidden
          ? <ScreenReaderOnly id={internalTitleId} text={title} startWithEmptySpace={false} />
          : <Typography variant="h4" fontWeight="bold" id={internalTitleId}>{title}</Typography>
        }
      </div>
      {content && (
        <div className={clsx(c.content, !title && c.withoutHeader)}>
          {content}
        </div>
      )}
    </>
  );

  const footer = customFooter ?? (hasActions ? (
    <div className={c.buttonBox}>
      {primaryAction}
      {secondaryAction}
    </div>
  ) : undefined);

  const closeHandler = isLoading ? undefined : onClose;

  if (isMobile && mobileVariant === 'modal') {
    return open ? createPortal(
      <MobileModal
        onClose={closeHandler}
        footer={footer}
        scrollRef={setContentEl}
        isScrollable={isScrollable}
        showTopBorder={showTopDivider}
        showBottomBorder={showBottomDivider}
        showCloseButton={!isLoading}
      >
        {scrollableContent}
      </MobileModal>,
      document.body
    ) : null;
  }

  return (
    <Dialog
      className={clsx(c.dialog, className, mobileVariant === 'drawer' && c.drawer)}
      open={open}
      closeHandler={closeHandler}
      size={MODAL_SIZE_WIDTH[size]}
      contentRef={setContentEl}
      footer={footer}
      hasScroll={isScrollable}
      showTopBorder={showTopDivider}
      showBottomBorder={showBottomDivider}
      showCloseIcon={!isLoading}
      disableBackdropClick={isLoading || disableBackdropClick}
      ariaLabelledBy={internalTitleId}
      ariaDescribedBy={ariaDescribedBy}
      contentWrapperClassName={c.modalDialogContentWrapper}
    >
      {scrollableContent}
    </Dialog>
  );
};
